import React, { useState, useEffect } from 'react';
import { Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, TablePagination, Grid, Select, MenuItem, Button } from '@mui/material';
import moment from 'moment';
import Sidebar from '../../components/Sidebar/Sidebar';
import DateRangePicker from '../../components/DateRangePicker/DateRangePicker';
import { searchPerformanceByDate } from '../../utils/apiUtils';
import './Performances.scss';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import TicketModal from '../../components/TicketModal/TicketModal';

const Performances = () => {
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [performances, setPerformances] = useState([]);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [page, setPage] = useState(0);
  const [chartData, setChartData] = useState([]);
  const [ticketModalOpen, setTicketModalOpen] = useState(false);
  const [selectedTicketId, setSelectedTicketId] = useState(null);
  const rowsPerPage = 30;

  const formatDate = (date) => {
    return date ? moment(date).format('DD-MM-YYYY HH:mm') : '';
  };

  useEffect(() => {
    const fetchPerformancesByDate = async () => {
      if (startDate && endDate) {
        try {
          let performancesData = await searchPerformanceByDate(startDate, endDate);

          let performancesDataForChart = performancesData.map(performance => ({
            ...performance,
            reply_date: moment(performance.reply_date).startOf('day').toDate()
          }));

          let performancesDataForTable = performancesData.map(performance => ({
            ...performance,
            reply_date: new Date(performance.reply_date)
          }));

          setPerformances(performancesDataForTable);

          let dates = performancesDataForChart.map(performance => performance.reply_date);
          dates = [...new Set(dates.map(date => +date))].map(date => new Date(date));
          dates.sort((a, b) => a - b);

          let agents = Array.from(new Set(performancesDataForChart.map(performance => performance.agent_name)));

          let chartData = dates.map(date => {
            let dateObj = { date: moment(date).format('DD-MM-YYYY') };

            agents.forEach(agent => {
              let performancesOnDate = performancesDataForChart.filter(performance => performance.agent_name === agent && +performance.reply_date === +date);
              dateObj[agent] = performancesOnDate.length;
            });

            return dateObj;
          });

          setChartData(chartData);
        } catch (error) {
          console.error('Error fetching performances:', error);
        }
      }
    };

    fetchPerformancesByDate();
  }, [startDate, endDate]);

  const handleStartDateChange = (date) => {
    setStartDate(date);
  };

  const handleEndDateChange = (date) => {
    setEndDate(date);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleOpenTicketModal = async (ticketId) => {
    setSelectedTicketId(ticketId);
    setTicketModalOpen(true);
  };

  const handleCloseTicketModal = () => {
    setTicketModalOpen(false);
  };

  const agents = Array.from(new Set(performances.map(performance => performance.agent_name)));

  const filteredPerformances = selectedAgent !== null ? performances.filter(performance => performance.agent_name === selectedAgent) : performances;

  const performancesCount = agents.filter(agent => agent).map(agent => {
    return { agent: agent, count: performances.filter(performance => performance.agent_name === agent).length };
  });

  // Helper function to calculate daily stats per agent
  const getDailyStatsPerAgent = () => {
    const agentStats = {};
    filteredPerformances.forEach((performance) => {
      const dateKey = moment(performance.reply_date).format('DD-MM-YYYY');
      const agentName = performance.agent_name;
      if (!agentStats[dateKey]) {
        agentStats[dateKey] = {};
      }
      if (!agentStats[dateKey][agentName]) {
        agentStats[dateKey][agentName] = 0;
      }
      agentStats[dateKey][agentName]++;
    });
    return agentStats;
  };
  const dailyStatsPerAgent = getDailyStatsPerAgent();
  const sortedAgents = agents.sort((a, b) => a.localeCompare(b));


  return (
    <Grid container justifyContent="center">
      <Sidebar />

      <Grid item xs={10}>
        <Typography variant="h2">Productivity</Typography>

        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Box className="StyledButtonContainer">
              <Box className="StyledCard">
                <Typography variant="h5">Date Range</Typography>
                <DateRangePicker
                  startDate={startDate}
                  endDate={endDate}
                  onStartDateChange={handleStartDateChange}
                  onEndDateChange={handleEndDateChange}
                />
              </Box>
              <Box className="StyledCard" style={{ height: "300px" }}>
                <Typography variant="h5">Select Agent</Typography>
                <Select
                  value={selectedAgent}
                  onChange={e => setSelectedAgent(e.target.value)}
                  className="SelectDropdown"
                >
                  <MenuItem value="Everyone">Everyone</MenuItem>
                  {agents.filter(agent => agent)
                    .map(agent => (
                      <MenuItem key={agent} value={agent}>
                        {agent}
                      </MenuItem>
                    ))}
                </Select>
              </Box>


              <Box mt={4} width="100%" marginBottom="40px"> {/* Set the width to 100% */}
                <Typography variant="h5">Day-by-day Stats per Agent</Typography>
                <TableContainer component={Paper} className="customTable">
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Date</TableCell>
                        <TableCell style={{ borderRight: '1px solid #ccc' }} /> {/* Add separator between Date and the first agent */}
                        {sortedAgents.map((agent, index) => (
                          <TableCell
                            key={agent}
                            style={{
                              width: `${100 / (sortedAgents.length + 1)}%`,
                              borderRight: index !== sortedAgents.length - 1 ? '1px solid #ccc' : 'none', // Add vertical separator
                            }}
                          >
                            {agent}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {Object.keys(dailyStatsPerAgent).map((dateKey) => (
                        <TableRow key={dateKey}>
                          <TableCell>{dateKey}</TableCell>
                          <TableCell style={{ borderRight: '1px solid #ccc' }} /> {/* Add separator between Date and the first agent */}
                          {sortedAgents.map((agent, index) => (
                            <TableCell
                              key={agent}
                              style={{
                                width: `${100 / (sortedAgents.length + 1)}%`,
                                borderRight: index !== sortedAgents.length - 1 ? '1px solid #ccc' : 'none', // Add vertical separator
                              }}
                            >
                              {dailyStatsPerAgent[dateKey][agent] || 0}
                            </TableCell>
                          ))}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>

              
              {/* CARD PER AGENT */}
              {/* {performancesCount.map(agentData => (
                <Box key={agentData.agent} className="StyledCard">
                  <Typography variant="h5">Score of {agentData.agent}</Typography>
                  <Typography variant="body2" className="count">{agentData.count}</Typography>
                </Box>
              ))} */}


            </Box>
            <Box className="StyledChartContainer">
              <Typography variant="h5">Productivity Chart</Typography>
              <ResponsiveContainer width="100%" height={400}>
                <LineChart
                  data={chartData}
                  margin={{
                    top: 5,
                    right: 30,
                    left: 20,
                    bottom: 5,
                  }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  {agents.map((agent, index) => (
                    <Line
                      type="monotone"
                      dataKey={agent}
                      stroke={`#${Math.floor(Math.random() * 16777215).toString(16)}`}
                      activeDot={{ r: 8 }}
                      key={index}
                    />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            </Box>
            
          </Grid>
        </Grid>

        <Box mt={4}>
          <Typography variant="h5">Performance Table</Typography>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Agent Name</TableCell>
                  <TableCell>Ticket ID</TableCell>
                  <TableCell>Ticket Name</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(rowsPerPage > 0
                  ? filteredPerformances.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  : filteredPerformances
                ).map((performance) => (
                  <TableRow key={performance.ticket_id}>
                    <TableCell>{performance.agent_name}</TableCell>
                    <TableCell>{performance.ticket_id}</TableCell>
                    <TableCell>{performance.ticket_name}</TableCell>
                    <TableCell>{formatDate(performance.reply_date)}</TableCell>
                      <TableCell>
                      <Button variant="contained" onClick={() => handleOpenTicketModal(performance.ticket_id)}>
                        More Details
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <TablePagination
              rowsPerPageOptions={[30]}
              component="div"
              count={filteredPerformances.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
            />
          </TableContainer>
        </Box>
      </Grid>

      {ticketModalOpen && (
        <TicketModal ticketId={selectedTicketId} onClose={handleCloseTicketModal} />
      )}
    </Grid>
  );
};

export default Performances;

